import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchLawyerPricing } from "@lawyer/services/lawyerApi";
import { LAWYER_BASE } from "@lawyer/constants/route";

const rupees = (paise) => `₹${Math.round((paise || 0) / 100).toLocaleString("en-IN")}`;

// Read-only summary of the Admin-assigned pricing level; details live on /pricing.
// `initialData`: page-level prefetched pricing payload (or { error: true }) — skips the mount-time fetch.
// `actionLabel`: optional bottom CTA text when a level is already assigned.
// `actionLabelUnassigned`: optional bottom CTA text when no level is assigned yet.
// `hideDetailsLink`: when an action label is set, hides the default top-right "View pricing" link.
export default function LevelCard({ initialData, actionLabel, actionLabelUnassigned, hideDetailsLink = false, className = "" }) {
  const [data, setData] = useState(initialData ?? null);
  useEffect(() => {
    if (initialData !== undefined) { setData(initialData); return; }
    fetchLawyerPricing().then(setData).catch(() => setData({ error: true }));
  }, [initialData]);
  if (!data || data.error) return null;
  const active = data.active;
  const live = data.live_pricing;
  const pending = data.pending_application;
  const pricingPath = `${LAWYER_BASE}/pricing`;

  const hasActiveLevel = active?.active_level != null;
  const resolvedActionLabel = hasActiveLevel ? actionLabel : actionLabelUnassigned;
  const actionButton = resolvedActionLabel ? (
    <Link to={pricingPath} data-testid="lawyer-level-action-btn" className="block mt-3">
      <Button variant="outline" className="rounded-xl font-bold border-[#0A2540] text-[#0A2540] w-full sm:w-auto h-9 text-xs">
        {resolvedActionLabel}
      </Button>
    </Link>
  ) : null;

  return (
    <section className={`rounded-2xl border border-slate-200 bg-white p-5 ${className}`} data-testid="lawyer-level-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">Your lawyer level</p>
          {active?.active_level ? (
            <>
              <div className="mt-1 flex flex-wrap items-baseline gap-x-3">
                <p className="text-2xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }} data-testid="lawyer-level-active">Level {active.active_level}</p>
                <p className="text-2xl font-extrabold text-[#D4AF37]" style={{ fontFamily: "Poppins" }} data-testid="lawyer-level-fee">{rupees(live?.lawyer_fee_paise ?? active.active_base_fee_paise)}</p>
              </div>
              <p className="text-xs text-slate-500">Video · 15 min base fee</p>
              <p className="text-[11px] text-slate-400 mt-1">Set by Advoset Admin{pending ? <span data-testid="lawyer-level-pending"> · change request pending: Level {pending.requested_level}</span> : null}</p>
            </>
          ) : (
            <div className="mt-2" data-testid="lawyer-level-none">
              {pending ? (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                  Your request for <b>Level {pending.requested_level}</b> is with the Admin team.
                </p>
              ) : (
                <>
                  <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                    {actionLabelUnassigned
                      ? "Your lawyer level has not been assigned yet."
                      : <>No level assigned yet. <Link to={pricingPath} className="font-bold underline">Request a level</Link> to unlock bookings.</>}
                  </p>
                  {actionButton}
                </>
              )}
            </div>
          )}
        </div>
        {!hideDetailsLink && (
          <Link to={pricingPath} className="text-xs font-bold text-[#0A2540] hover:underline shrink-0 inline-flex items-center gap-1" data-testid="lawyer-level-details-link">
            <Layers size={12} /> View pricing →
          </Link>
        )}
      </div>
      {active?.active_level && actionButton}
    </section>
  );
}
